import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { LoanApiService } from './loan-api.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  isLoggedIn: boolean = false;
  private isAuthenticatedSubject$ = new Subject<boolean>();
  isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
    private loanApiService: LoanApiService
  ) {}

  getPrivacyAcceptedStatus(): Observable<{
    is_privacy_policy_accepted: boolean;
  }> {
    return this.http.get<{ is_privacy_policy_accepted: boolean }>(
      `${environment.apiUrl}loan-review/extraction/get-user-privacy-consent`
    );
  }

  setPrivacyAcceptedStatus(accepted: boolean): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}loan-review/extraction/update-user-privacy-consent?is_privacy_policy_accepted=${accepted}`,
      {}
    );
  }

  // getPrivacyAcceptedStatus(): Observable<{ is_privacy_policy_accepted: boolean }> {
  //   return this.http.get<{ is_privacy_policy_accepted: boolean }>(`${environment.apiUrl}loan-review/extraction/get-user-privacy-consent`);
  // }

  // setPrivacyAcceptedStatus(accepted: boolean): Observable<any> {
  //   return this.http.put(
  //     `${environment.apiUrl}loan-review/extraction/update-user-privacy-consent?is_privacy_policy_accepted=${accepted}`,
  //     {}
  //   );
  // }

  getProfilePhoto(): Observable<Blob> {
    const account = this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No active account found');
    }

    // Return an observable that emits the profile photo blob after acquiring the token
    return new Observable<Blob>((observer) => {
      this.msalService.instance
        .acquireTokenSilent({
          account,
          scopes: ['User.Read'], // Adjust scopes as needed
        })
        .then((result) => {
          const accessToken = result.accessToken;
          this.http
            .get(environment.profileUrl, {
              responseType: 'blob',
              headers: { Authorization: `Bearer ${accessToken}` },
            })
            .subscribe({
              next: (blob) => {
                observer.next(blob);
                observer.complete();
              },
              error: (err) => observer.error(err),
            });
        })
        .catch((err) => observer.error(err));
    });
  }

  getUsername(): string {
    const activeAccount = this.msalService.instance.getAllAccounts()[0] || null;
    if (activeAccount) {
      return activeAccount.name ?? '';
    }
    return '';
  }

  getUserEmail(): string {
    const activeAccount = this.msalService.instance.getAllAccounts()[0] || null;
    if (activeAccount) {
      return activeAccount.username ?? '';
    }
    return '';
  }

  loggedIn() {
    // Perform login logic
    this.isAuthenticatedSubject$.next(true);
    this.isLoggedIn = true;
    // create a new guid and save it to session storage with name chat-id
    const chatId = crypto.randomUUID();
    sessionStorage.setItem('chat-id', chatId);
  }

  loggedOut() {
    const token =
      sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token) {
      this.loanApiService.logout(token).subscribe({
        next: () => this.finalizeLogout(),
        error: () => this.finalizeLogout(),
      });
    } else {
      this.finalizeLogout();
    }
    this.http.post(environment.apiUrl + 'logout', {}).subscribe({
      next: () => {},
      error: (err) => {},
    });
  }

  private finalizeLogout(): void {
    this.isAuthenticatedSubject$.next(false);
    this.isLoggedIn = false;

    // Clear session storage
    this.loanApiService.clearToken();
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('chat-id');
    sessionStorage.removeItem('prpic');
    // Remove all cookies from the browser
    // document.cookie.split(';').forEach((c) => {
    //   document.cookie = c
    //     .replace(/^ +/, '')
    //     .replace(/=.*/, '=;expires=' + new Date(0).toUTCString() + ';path=/');
    // });
    // Safely remove cookies: validate cookie name to avoid injection,
    // and set secure attributes when clearing.
    // Prefer explicit whitelist of cookie names to delete to avoid
    // any possibility of deleting or manipulating attacker-controlled names.
    // Only clear cookies that are client-accessible; do NOT attempt to clear HttpOnly cookies here.
    // The server logout endpoint must clear HttpOnly session cookies via Set-Cookie with HttpOnly flag.
    
    // const clientCookiesToClear = ['chat-id', 'prpic'];
    // clientCookiesToClear.forEach((name) => {
    //   if (/^[A-Za-z0-9_\-]+$/.test(name)) {
    //     document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict;Secure`;
    //   }
    // });

    sessionStorage.removeItem('chat-id');
    sessionStorage.removeItem('prpic');
    // Invalidate session in Azure AD as well
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  getChatId(): string {
    // Retrieve the chat ID from session storage
    return sessionStorage.getItem('chat-id') || '';
  }
  isAuthenticated(): boolean {
    // check msal service for active account
    const activeAccount = this.msalService.instance.getAllAccounts()[0] || null;
    return !!activeAccount;
  }

  saveBlobToSessionStorage(key: string, blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          sessionStorage.setItem(key, reader.result);
          resolve();
        } else {
          reject(new Error('Failed to convert Blob to string.'));
        }
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  getBlobFromSessionStorage(key: string): Blob | null {
    const base64String = sessionStorage.getItem(key);
    if (base64String) {
      // Extract the Base64 data part (remove "data:image/png;base64," prefix)
      const parts = base64String.split(';base64,');
      if (parts.length === 2) {
        const contentType = parts[0].substring(5); // e.g., "image/png"
        const raw = window.atob(parts[1]); // Decode Base64
        const rawLength = raw.length;
        const uInt8Array = new Uint8Array(rawLength);

        for (let i = 0; i < rawLength; ++i) {
          uInt8Array[i] = raw.charCodeAt(i);
        }

        return new Blob([uInt8Array], { type: contentType });
      }
    }
    return null;
  }
}
